import { useQuery, useMutation, useQueryClient } from "react-query";
import { useState, useEffect, useCallback } from "react";
import {
  getWhatsAppConnections,
  connectWhatsAppInstance,
  disconnectWhatsAppInstance,
  restartWhatsAppInstance
} from "../../services/api/whatsapp/whatsappApi";
import webSocketService from "../../services/websocket/websocketService";

/**
 * Hooks para gerenciar conexões WhatsApp Evolution API
 * Seguindo padrão de hooks de API do projeto JBE Engenharia
 */

const whatsappApi = {
  fetchWhatsAppConnections: async () => {
    try {
      const { data } = await getWhatsAppConnections();
      return data;
    } catch (error) {
      console.warn("Erro ao consultar conexões WhatsApp:", error.message);
      throw error;
    }
  },
  
  connectInstance: async (connectionId) => {
    try {
      const { data } = await connectWhatsAppInstance(connectionId);
      return data;
    } catch (error) {
      console.error("Erro ao conectar instância WhatsApp:", error.message);
      throw error;
    }
  },
  
  disconnectInstance: async (connectionId) => {
    try {
      const { data } = await disconnectWhatsAppInstance(connectionId);
      return data;
    } catch (error) {
      console.error("Erro ao desconectar instância WhatsApp:", error.message);
      throw error;
    }
  },
  
  restartInstance: async (connectionId) => {
    try {
      const { data } = await restartWhatsAppInstance(connectionId);
      return data;
    } catch (error) {
      console.error("Erro ao reiniciar instância WhatsApp:", error.message);
      throw error;
    }
  },
};

// Hook híbrido: WebSocket + Polling fallback
export const useWhatsAppConnectionsHybrid = () => {
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [localConnections, setLocalConnections] = useState(null);
  const queryClient = useQueryClient();

  // Query com polling condicional (apenas quando WebSocket desconectado)
  const { 
    isLoading: connectionsLoading, 
    data: connections, 
    error: connectionsError,
    refetch: refetchConnections
  } = useQuery(
    "whatsappConnectionsQuery",
    whatsappApi.fetchWhatsAppConnections,
    {
      refetchInterval: isWebSocketConnected ? false : 30000, // 30s apenas se WS desconectado
      refetchIntervalInBackground: false,
      retry: 1,
      refetchOnWindowFocus: true,
      onError: (error) => {
        console.warn("WhatsApp Connections API Error:", error);
      },
    }
  );

  // Handler para atualizações via WebSocket
  const handleWhatsAppStatusUpdate = useCallback((data) => {
    console.log('WhatsApp status update recebido via WebSocket:', data);
    
    // Atualizar estado local imediatamente
    if (localConnections) {
      const updatedConnections = localConnections.map(conn => 
        conn.id === data.connectionId 
          ? { ...conn, status: data.status, phone: data.phone, updatedAt: data.timestamp }
          : conn
      );
      setLocalConnections(updatedConnections);
      
      // Atualizar cache do React Query
      queryClient.setQueryData("whatsappConnectionsQuery", updatedConnections);
    }
  }, [localConnections, queryClient]);

  // Configurar WebSocket
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    webSocketService.connect(token, {
      onConnect: () => {
        console.log('WebSocket conectado - WhatsApp polling desabilitado');
        setIsWebSocketConnected(true);
      },
      onDisconnect: (reason) => {
        console.log('WebSocket desconectado - WhatsApp polling habilitado:', reason);
        setIsWebSocketConnected(false);
      },
      onError: (error) => {
        console.error('WebSocket error:', error);
        setIsWebSocketConnected(false);
      }
    });

    // Listener para WhatsApp status updates
    const socket = webSocketService.getSocket();
    if (socket) {
      socket.on('whatsappConnectionStatusChanged', handleWhatsAppStatusUpdate);
    }

    return () => {
      if (socket) {
        socket.off('whatsappConnectionStatusChanged', handleWhatsAppStatusUpdate);
      }
    };
  }, [handleWhatsAppStatusUpdate]);

  // Sincronizar dados locais com query
  useEffect(() => {
    if (connections) {
      setLocalConnections(connections);
    }
  }, [connections]);

  return {
    connectionsLoading,
    connections: localConnections || connections,
    connectionsError,
    refetchConnections,
    isWebSocketConnected
  };
};

// Hook para listar conexões com polling automático (LEGACY - manter compatibilidade)
export const useWhatsAppConnections = () => {
  const { 
    isLoading: connectionsLoading, 
    data: connections, 
    error: connectionsError,
    refetch: refetchConnections
  } = useQuery(
    "whatsappConnectionsQuery",
    whatsappApi.fetchWhatsAppConnections,
    {
      refetchInterval: 10000, // Polling a cada 10 segundos
      refetchIntervalInBackground: false,
      retry: 1,
      refetchOnWindowFocus: true, // Atualiza ao focar na janela
      onError: (error) => {
        console.warn("WhatsApp Connections API Error:", error);
      },
    }
  );
  
  return {
    connectionsLoading,
    connections,
    connectionsError,
    refetchConnections
  };
};

// Hook para conectar instância
export const useConnectWhatsApp = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    whatsappApi.connectInstance,
    {
      onSuccess: () => {
        // Invalida cache para atualizar lista
        queryClient.invalidateQueries("whatsappConnectionsQuery");
      },
      onError: (error) => {
        console.error("Mutation error - Connect WhatsApp:", error);
      },
    }
  );
};

// Hook para desconectar instância
export const useDisconnectWhatsApp = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    whatsappApi.disconnectInstance,
    {
      onSuccess: () => {
        queryClient.invalidateQueries("whatsappConnectionsQuery");
      },
      onError: (error) => {
        console.error("Mutation error - Disconnect WhatsApp:", error);
      },
    }
  );
};

// Hook para reiniciar instância
export const useRestartWhatsApp = () => {
  const queryClient = useQueryClient();
  
  return useMutation(
    whatsappApi.restartInstance,
    {
      onSuccess: () => {
        queryClient.invalidateQueries("whatsappConnectionsQuery");
      },
      onError: (error) => {
        console.error("Mutation error - Restart WhatsApp:", error);
      },
    }
  );
};

// Legacy hook - mantém compatibilidade
export const useWhatsAppStatus = () => {
  const { connectionsLoading, connections, connectionsError } = useWhatsAppConnections();
  
  return {
    whatsappStatusLoading: connectionsLoading,
    whatsappStatus: connections,
    whatsappStatusError: connectionsError
  };
}; 